import * as React from 'react';
import { Dialog, Transition } from '@headlessui/react';

import { cn } from 'lib/utils';

import './modalViewport.css';

type ModalSize = 'sm' | 'md' | 'lg';

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  /** Called after the leave transition finishes; use to clear data without flashing empty content */
  onCloseComplete?: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Optional icon element (e.g. for DialogBox); rendered in header left of title */
  icon?: React.ReactNode;
  size?: ModalSize;
  children: React.ReactNode;
  footer?: React.ReactNode;
};

export function Modal({
  open,
  onClose,
  onCloseComplete,
  title,
  description,
  icon,
  size = 'md',
  children,
  footer,
}: ModalProps) {
  const maxW =
    size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-lg';

  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeCompleteCalledRef = React.useRef(false);

  const handleCloseComplete = React.useCallback(() => {
    if (closeCompleteCalledRef.current) return;
    closeCompleteCalledRef.current = true;
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    onCloseComplete?.();
  }, [onCloseComplete]);

  React.useEffect(() => {
    if (open) {
      closeCompleteCalledRef.current = false;
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }
    if (onCloseComplete == null) return;
    timeoutRef.current = setTimeout(handleCloseComplete, 150);
    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [open, onCloseComplete, handleCloseComplete]);

  const afterLeave = React.useCallback(() => {
    handleCloseComplete();
  }, [handleCloseComplete]);

  return (
    <Transition appear show={open} as={React.Fragment} afterLeave={afterLeave}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto c7-modal-scroll">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.98]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.98]"
            >
              <Dialog.Panel
                className={cn(
                  'w-full transform overflow-hidden rounded-2xl bg-card text-card-foreground shadow-xl border border-border text-left align-middle transition-all flex flex-col c7-modal-panel',
                  maxW
                )}
              >
                {(title || description || icon) && (
                  <div className="p-4 pb-3 border-b border-border flex-shrink-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex flex-1 items-start gap-3">
                        {icon && <div className="flex-shrink-0">{icon}</div>}
                        <div className="min-w-0 flex-1">
                          {title && (
                            <Dialog.Title className="text-base md:text-lg font-semibold tracking-tight">
                              {title}
                            </Dialog.Title>
                          )}
                          {description && (
                            <div className="mt-1 text-xs md:text-sm text-muted-foreground">
                              {description}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        aria-label="Close"
                      >
                        <i className="bi bi-x-lg text-base" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="p-4 flex-1 min-h-0 overflow-y-auto">{children}</div>

                {footer && (
                  <div className="p-4 pt-3 border-t border-border flex items-center justify-end gap-2 flex-shrink-0">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

